import { useEffect } from "react";
import { NextPage } from "next";
import { useDispatch, useSelector } from "react-redux";

import getUser from "api/requests/getUser";
import { useCartErrorState } from "context/CartErrorContext";
import useCartItemErrors from "hooks/useCartItemErrors";
import { useIsJaneCheckoutEnabled } from "hooks/useIsJaneCheckoutEnabled";
import { loadCheckoutDisclaimerThunk } from "redux/action-creators/cartDispensary";
import { DISPENSARY_LOAD } from "redux/reducers/dispensary";
import { SET_USER_DELIVERY_ADDRESS } from "redux/reducers/location";
import { USER_LOAD } from "redux/reducers/user";
import {
  getCartContext,
  getCartItems,
  getDealGroups,
} from "redux/selectors/cart";
import { getCartDispensaryData } from "redux/selectors/cartDispensary";
import { getDomainCountryCode } from "redux/selectors/config";
import getCartAndDispensary from "utils/getCartAndDispensary";
import getReqCookies from "utils/getReqCookies";
import { getUserLocationInfoFromCookie } from "utils/getUserLocationFromCookie";

import Bag from "components/Checkout/Bag";
import CheckoutLayout from "components/Checkout/CheckoutLayout";
import { JaneCheckout } from "components/Checkout/JaneCheckout";

type Props = {
  referrer: string | null;
  menuType: string | null;
  isAndroidNative: boolean;
};

const BagPage: NextPage<Props> = ({ referrer, menuType, isAndroidNative }) => {
  const dispatch = useDispatch();
  const isJaneCheckoutEnabled = useIsJaneCheckoutEnabled();
  const cartItems = useSelector(getCartItems);
  const dealGroups = useSelector(getDealGroups);
  const dispensary = useSelector(getCartDispensaryData);

  const cartItemErrors = useCartItemErrors();
  const { setErrors } = useCartErrorState();
  const cartItemsIds = cartItems.map((item) => item.id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  const cartItemErrorsForThisCart = cartItemErrors.filter((err: any) =>
    cartItemsIds.includes(err.id)
  );
  if (process.browser && setErrors && !cartItemErrorsForThisCart.length) {
    setErrors([]);
  }

  useEffect(() => {
    dispatch({
      dispensary,
      type: DISPENSARY_LOAD,
    });
  }, [dispensary]);

  return (
    <CheckoutLayout
      className="m-none md:m-auto"
      indexInSearchEngines={true}
      isAndroidNative={isAndroidNative}
      menuType={menuType}
      referrer={referrer}
    >
      {isJaneCheckoutEnabled ? (
        <div className="container">
          <JaneCheckout
            cartItems={cartItems}
            cartItemErrors={cartItemErrorsForThisCart}
          />
        </div>
      ) : (
        <Bag
          cartItems={cartItems}
          cartItemErrors={cartItemErrorsForThisCart}
          dealGroups={dealGroups}
        />
      )}
    </CheckoutLayout>
  );
};

BagPage.getInitialProps = async (context) => {
  const { store, req, query } = context;

  if (req) {
    store.dispatch({
      forDelivery: query.forDelivery === "true",
      type: "SET_FOR_DELIVERY_OPTION",
    });
    const domainCountryCode = getDomainCountryCode(store.getState());

    /** Look for 'leafly-location' cookie and if it exists,
     *  set deliveryAddress state */
    // TODO: figure out why cookie is being reset on page refresh
    const addressFromLocationCookie = getUserLocationInfoFromCookie(
      req.headers.cookie
    );

    if (addressFromLocationCookie) {
      store.dispatch({
        address: addressFromLocationCookie,
        type: SET_USER_DELIVERY_ADDRESS,
      });
    }
    const cartContext = getCartContext(store.getState());
    const cartId = query?.cartId || getReqCookies(req)["X-Leafly-Id"] || null;

    const [_, user] = await Promise.all([
      getCartAndDispensary({
        cartContext: cartContext,
        cartId: cartId as string,
        dispatch: store.dispatch,
        domainCountryCode: domainCountryCode,
        request: req,
        updateOnLoad: query.refresh === "1",
      }),
      getUser(domainCountryCode, req?.headers.cookie),
    ]);

    store.dispatch({
      type: USER_LOAD,
      userData: user,
    });

    await store.dispatch(loadCheckoutDisclaimerThunk());
  }

  return {
    disableCheckoutBanner: true,
    disableGoogleMaps: true,
    isAndroidNative: req?.headers.referer === "android-app://leafly.android",
    menuType: Array.isArray(query.menuType)
      ? query.menuType[0]
      : query.menuType || null,
    referrer: Array.isArray(query.referrer)
      ? query.referrer[0]
      : query.referrer || null,
    showMinimalHeader: true,
  };
};

export default BagPage;
